import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEO from "@/components/base/SEO";

const memberTypes = [
  {
    id: "factory",
    icon: "ri-building-2-line",
    label: "Verified Factory",
    color: "text-orange-500",
    bg: "bg-orange-50",
    border: "border-orange-400",
    accent: "bg-orange-500",
    badge: "Most Popular",
    badgeBg: "bg-orange-500",
    price: "$8",
    priceSub: "/month after 3-month free trial",
    trialNote: "First 3 months FREE — no credit card required",
    trialIcon: "ri-gift-line",
    trialColor: "text-orange-600",
    trialBg: "bg-orange-50 border-orange-100",
    description: "For manufacturers, factories, and production facilities",
    perks: [
      "Verified Factory badge on all listings",
      "Upload unlimited verified products",
      "Access to local & overseas warehouse dropshipping",
      "Set your own commission % for resellers & stars",
      "Order management dashboard with tracking",
      "Buyer email & phone notification system",
      "Analytics: views, sales, revenue reports",
      "Priority placement in marketplace search",
      "Access to buyer requirements & leads",
      "Direct messaging with buyers worldwide",
    ],
    cta: "Start Free 3-Month Trial",
    ctaLink: "/auth",
    sub: "Cancel anytime · No hidden fees",
  },
  {
    id: "warehouse",
    icon: "ri-store-3-line",
    label: "Local Warehouse",
    color: "text-sky-500",
    bg: "bg-sky-50",
    border: "border-sky-400",
    accent: "bg-sky-500",
    badge: "USA Only",
    badgeBg: "bg-sky-500",
    price: "$8",
    priceSub: "/month after 3-month free trial",
    trialNote: "First 3 months FREE — no credit card required",
    trialIcon: "ri-gift-line",
    trialColor: "text-sky-600",
    trialBg: "bg-sky-50 border-sky-100",
    description: "For USA-based local warehouses and fulfillment centers",
    perks: [
      "Verified Local Warehouse badge",
      "List your warehouse on the platform",
      "Receive dropship orders from verified factories",
      "Manage inventory & fulfillment dashboard",
      "Connect with factories needing USA storage",
      "Real-time stock & shipment tracking",
      "Priority listing in warehouse directory",
      "Access to factory partnership requests",
      "Analytics: orders, fulfillment rate, revenue",
      "Direct messaging with factory partners",
    ],
    cta: "Register Your Warehouse",
    ctaLink: "/auth",
    sub: "Cancel anytime · No hidden fees",
  },
  {
    id: "reseller",
    icon: "ri-links-line",
    label: "Verified Reseller",
    color: "text-emerald-500",
    bg: "bg-emerald-50",
    border: "border-emerald-400",
    accent: "bg-emerald-500",
    badge: "Free Forever",
    badgeBg: "bg-emerald-500",
    price: "FREE",
    priceSub: "forever — $0/month always",
    trialNote: "Earn commission instead — no fees ever",
    trialIcon: "ri-heart-line",
    trialColor: "text-emerald-600",
    trialBg: "bg-emerald-50 border-emerald-100",
    description: "For resellers who promote and sell verified factory products",
    perks: [
      "Verified Reseller badge on your profile",
      "Promote verified factory products only",
      "Earn commission set by each factory (5–15%)",
      "Unique referral links per product",
      "Local & overseas warehouse dropshipping",
      "Real-time sales & commission tracker",
      "PayPal commission payouts (min $10)",
      "Works on TikTok, YouTube, Facebook, Instagram",
      "Access to exclusive factory promotions",
      "Reseller leaderboard & bonus rewards",
    ],
    cta: "Join Free as Reseller",
    ctaLink: "/auth",
    sub: "No credit card · No monthly fee · Ever",
  },
  {
    id: "star",
    icon: "ri-star-line",
    label: "Verified Star",
    color: "text-rose-500",
    bg: "bg-rose-50",
    border: "border-rose-400",
    accent: "bg-rose-500",
    badge: "Free Forever",
    badgeBg: "bg-rose-500",
    price: "FREE",
    priceSub: "forever — $0/month always",
    trialNote: "Earn commission instead — no fees ever",
    trialIcon: "ri-heart-line",
    trialColor: "text-rose-600",
    trialBg: "bg-rose-50 border-rose-100",
    description: "For influencers, content creators & social media stars",
    perks: [
      "Verified Star badge on your profile",
      "Promote verified factory products to your audience",
      "Higher commission rates (up to 20%)",
      "Exclusive brand collaboration opportunities",
      "Free product samples from partner factories",
      "Unique referral links per product",
      "Real-time earnings & analytics dashboard",
      "PayPal commission payouts (min $10)",
      "Works on TikTok, YouTube, Douyin, WeChat, Instagram",
      "Star leaderboard — top stars earn $10K+/mo",
    ],
    cta: "Join Free as Star",
    ctaLink: "/auth",
    sub: "No credit card · No monthly fee · Ever",
  },
];

const verificationSteps = [
  {
    icon: "ri-file-list-3-line",
    title: "Submit Documents",
    desc: "Upload your business registration, licenses, and certifications. All document types accepted.",
    color: "bg-orange-50 text-orange-500",
  },
  {
    icon: "ri-search-eye-line",
    title: "Our Team Reviews",
    desc: "Our verification team checks your documents within 24–48 hours. We verify authenticity and legitimacy.",
    color: "bg-sky-50 text-sky-500",
  },
  {
    icon: "ri-shield-check-line",
    title: "Get Verified Badge",
    desc: "Once approved, your Verified badge appears on all your listings, building instant buyer trust.",
    color: "bg-emerald-50 text-emerald-500",
  },
  {
    icon: "ri-rocket-line",
    title: "Start Earning",
    desc: "Your first 3 months are completely free. After that, just $8/month to keep your verified status.",
    color: "bg-rose-50 text-rose-500",
  },
];

const faqs = [
  { q: "Who needs to be verified?", a: "All member types — Factories, Local Warehouses, Resellers, and Stars — must complete verification before they can list products, access buyer leads, or earn commissions. Verification protects buyers and ensures platform quality." },
  { q: "How long does verification take?", a: "Our team reviews submitted documents within 24–48 hours. You will receive an email notification once your verification is approved or if additional documents are needed." },
  { q: "When does the free trial end for factories and warehouses?", a: "Your first 3 months are completely free — no credit card required. After the trial, it is just $8/month. You can cancel anytime with no penalty." },
  { q: "Why is verification required for resellers and stars?", a: "Verification ensures that only real, legitimate resellers and influencers promote our factory products. This protects factories from fraud and ensures buyers receive genuine products." },
  { q: "Can resellers and stars earn without paying?", a: "Yes! Resellers and Stars are always free — $0/month forever. You earn commission (5–20%) set by each factory and get paid via PayPal. No fees, no subscriptions." },
  { q: "What documents are needed for verification?", a: "Factories need business registration + certifications (ISO, FDA, CE, etc.). Warehouses need facility license + address proof. Resellers and Stars need government ID + social media profile link." },
  { q: "Can I cancel my factory or warehouse membership?", a: "Yes, you can cancel anytime. Your verified badge and listings remain active until the end of your current billing period. No cancellation fees." },
];

const platforms = [
  { name: "TikTok", icon: "ri-tiktok-line", color: "bg-gray-900 text-white" },
  { name: "YouTube", icon: "ri-youtube-line", color: "bg-red-500 text-white" },
  { name: "Facebook", icon: "ri-facebook-fill", color: "bg-sky-600 text-white" },
  { name: "Instagram", icon: "ri-instagram-line", color: "bg-rose-500 text-white" },
  { name: "Twitter / X", icon: "ri-twitter-x-line", color: "bg-gray-800 text-white" },
  { name: "Douyin", icon: "ri-tiktok-line", color: "bg-black text-white" },
  { name: "WeChat", icon: "ri-wechat-line", color: "bg-green-500 text-white" },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const membershipSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Membership & Verification — StarBookPrime",
    "description": "All factories, local warehouses, resellers, and stars must be verified. Factories & warehouses get 3 months free then $8/month. Resellers & Stars are free forever.",
    "url": `${SITE_URL}/membership`,
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  },
];

export default function MembershipPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Verified Membership — Factory & Warehouse $8/mo, Resellers & Stars Free | StarBookPrime"
        description="All factories, local warehouses, resellers, and stars must be verified. Factories & warehouses get 3 months free then $8/month. Resellers & Stars are free forever."
        keywords="verified factory membership, local warehouse membership, reseller free membership, star influencer platform"
        canonical="/membership"
        schema={membershipSchema}
      />
      <Navbar />
      <div className="pt-16">

        {/* Hero */}
        <div className="relative overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/5a899540963a50f0641f6d679df1966e.jpg"
            alt="Verified Membership"
            className="w-full h-72 md:h-96 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-transparent flex items-center">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
              <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4">Verification Required</span>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight max-w-2xl">
                Every Member Must Be<br />
                <span className="text-orange-400">Verified to Participate</span>
              </h1>
              <p className="text-white/80 text-base md:text-lg max-w-xl mb-6">
                Factories, Local Warehouses, Resellers, and Stars — all must pass verification. This protects buyers and ensures every transaction is safe and legitimate.
              </p>
              <div className="flex flex-wrap gap-3">
                {["✓ Verified Factories", "✓ Verified Warehouses", "✓ Verified Resellers", "✓ Verified Stars"].map((t) => (
                  <span key={t} className="bg-white/15 border border-white/25 text-white text-xs font-semibold px-4 py-2 rounded-full backdrop-blur-sm">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">

          {/* Verification Requirement Banner */}
          <div className="bg-gray-900 rounded-2xl p-6 md:p-8 mb-16 flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-500 rounded-2xl flex-shrink-0">
              <i className="ri-shield-check-line text-white text-3xl" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-xl font-extrabold text-white mb-1">Verification is Mandatory for All Members</h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                Whether you are a factory, local warehouse, reseller, or star — you must complete our verification process before you can list products, access buyer leads, or earn commissions. This keeps our platform safe, trusted, and fraud-free.
              </p>
            </div>
            <Link
              to="/auth"
              className="flex-shrink-0 px-7 py-3.5 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-shield-check-line mr-2" />Start Verification
            </Link>
          </div>

          {/* How Verification Works */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-extrabold text-gray-900 mb-2">How Verification Works</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">Simple 4-step process. Most members get verified within 24 hours.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {verificationSteps.map((s, i) => (
                <div key={s.title} className="relative text-center">
                  {i < 3 && <div className="hidden lg:block absolute top-7 left-[calc(50%+28px)] right-0 h-0.5 bg-gray-100" />}
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4 relative ${s.color}`}>
                    <i className={`${s.icon} text-2xl`} />
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-gray-900 text-white text-xs font-bold rounded-full">{i + 1}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{s.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Cards — 4 types */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Choose Your Member Type</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">All member types require verification. Factories & Warehouses get 3 months free, then $8/month. Resellers & Stars are always free.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {memberTypes.map((m) => (
                <div key={m.id} className={`relative rounded-2xl border-2 ${m.border} overflow-hidden flex flex-col`}>
                  {/* Top accent bar */}
                  <div className={`h-1 w-full ${m.accent}`} />

                  <div className="p-6 flex flex-col flex-1">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${m.bg}`}>
                          <i className={`${m.icon} ${m.color} text-xl`} />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-gray-900 text-sm leading-tight">{m.label}</h3>
                          <p className="text-xs text-gray-400 mt-0.5">{m.description}</p>
                        </div>
                      </div>
                    </div>
                    <span className={`self-start text-xs font-bold text-white px-3 py-1 rounded-full mb-4 ${m.badgeBg}`}>{m.badge}</span>

                    {/* Price */}
                    <div className={`rounded-xl p-4 border mb-5 ${m.trialBg}`}>
                      <div className="flex items-end gap-1.5 mb-1">
                        <span className="text-3xl font-extrabold text-gray-900">{m.price}</span>
                        {m.price !== "FREE" && <span className="text-gray-500 text-xs mb-1">{m.priceSub}</span>}
                        {m.price === "FREE" && <span className="text-gray-500 text-xs mb-1">forever</span>}
                      </div>
                      <p className={`text-xs font-semibold ${m.trialColor} flex items-center gap-1`}>
                        <i className={m.trialIcon} />{m.trialNote}
                      </p>
                    </div>

                    {/* Perks */}
                    <ul className="space-y-2 mb-6 flex-1">
                      {m.perks.map((perk) => (
                        <li key={perk} className="flex items-start gap-2 text-xs text-gray-700">
                          <i className={`ri-checkbox-circle-fill ${m.color} mt-0.5 flex-shrink-0`} />
                          {perk}
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Link
                      to={m.ctaLink}
                      className={`block w-full text-center py-3 ${m.accent} text-white font-bold rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap text-sm`}
                    >
                      {m.cta}
                    </Link>
                    <p className="text-center text-xs text-gray-400 mt-2">{m.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing comparison table */}
          <div className="mb-16 overflow-x-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Pricing at a Glance</h2>
            </div>
            <table className="w-full min-w-[600px] border-collapse">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="text-left px-5 py-4 text-sm font-bold rounded-tl-xl">Member Type</th>
                  <th className="text-center px-5 py-4 text-sm font-bold">Verification</th>
                  <th className="text-center px-5 py-4 text-sm font-bold">Free Trial</th>
                  <th className="text-center px-5 py-4 text-sm font-bold">Monthly Fee</th>
                  <th className="text-center px-5 py-4 text-sm font-bold rounded-tr-xl">Commission</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { type: "Verified Factory", icon: "ri-building-2-line", color: "text-orange-500", verify: true, trial: "3 months free", fee: "$8/mo", commission: "Sets own rate" },
                  { type: "Local Warehouse", icon: "ri-store-3-line", color: "text-sky-500", verify: true, trial: "3 months free", fee: "$8/mo", commission: "Fulfillment fees" },
                  { type: "Verified Reseller", icon: "ri-links-line", color: "text-emerald-500", verify: true, trial: "Always free", fee: "$0 forever", commission: "5–15% per sale" },
                  { type: "Verified Star", icon: "ri-star-line", color: "text-rose-500", verify: true, trial: "Always free", fee: "$0 forever", commission: "Up to 20% per sale" },
                ].map((row, i) => (
                  <tr key={row.type} className={`border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <i className={`${row.icon} ${row.color} text-base`} />
                        <span className="text-sm font-semibold text-gray-900">{row.type}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
                        <i className="ri-shield-check-fill" />Required
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center text-sm text-gray-700 font-medium">{row.trial}</td>
                    <td className="px-5 py-4 text-center">
                      <span className={`text-sm font-extrabold ${row.fee === "$0 forever" ? "text-emerald-600" : "text-orange-600"}`}>{row.fee}</span>
                    </td>
                    <td className="px-5 py-4 text-center text-xs text-gray-600 font-medium">{row.commission}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Supported Platforms */}
          <div className="mb-16 text-center">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Sell on Every Platform</h2>
            <p className="text-gray-500 text-sm mb-8 max-w-xl mx-auto">Verified resellers and stars from all major platforms are welcome. Promote verified factory products and earn commission via PayPal.</p>
            <div className="flex flex-wrap justify-center gap-3">
              {platforms.map((p) => (
                <div key={p.name} className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold ${p.color}`}>
                  <i className={`${p.icon} text-base`} />
                  {p.name}
                </div>
              ))}
            </div>
          </div>

          {/* How Commission Works */}
          <div className="mb-16 bg-gradient-to-br from-orange-50 to-rose-50 rounded-2xl p-8 md:p-12 border border-orange-100">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2 text-center">How Commission &amp; Dropshipping Works</h2>
            <p className="text-gray-500 text-sm text-center mb-10 max-w-xl mx-auto">Each verified factory sets their own commission rate. You promote, customer orders, factory ships — you earn.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { step: "1", icon: "ri-link-m", title: "Get Your Link", desc: "Each product has a unique referral link tied to your verified account. Share it anywhere." },
                { step: "2", icon: "ri-shopping-cart-line", title: "Customer Orders", desc: "A customer clicks your link and places an order. Factory ships from local or overseas warehouse." },
                { step: "3", icon: "ri-truck-line", title: "Factory Dropships", desc: "The verified factory handles packaging and shipping directly. You never touch inventory." },
                { step: "4", icon: "ri-paypal-line", title: "You Get Paid", desc: "Commission is credited to your account and paid out via PayPal automatically. Min payout: $10." },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl border border-orange-200 mx-auto mb-4 relative">
                    <i className={`${s.icon} text-orange-500 text-2xl`} />
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full">{s.step}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{s.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-semibold text-gray-900 pr-4">{faq.q}</span>
                    <i className={`${openFaq === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl flex-shrink-0`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gray-900 rounded-2xl p-10 md:p-14">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-500 rounded-2xl mx-auto mb-5">
              <i className="ri-shield-check-line text-white text-3xl" />
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">Get Verified &amp; Start Earning</h2>
            <p className="text-gray-400 text-sm mb-8 max-w-lg mx-auto">Factories &amp; Warehouses get 3 months free, then just $8/month. Resellers &amp; Stars join free forever. All members must be verified.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
              <Link to="/auth" className="px-8 py-3.5 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-building-2-line mr-2" />Register as Factory / Warehouse
              </Link>
              <Link to="/auth" className="px-8 py-3.5 bg-rose-500 text-white font-bold rounded-full hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-star-line mr-2" />Join Free as Reseller / Star
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
